import { ref } from 'vue'

/**
 * How a Ticket Protection change ended. `cartMissing` = the cart is gone
 * server-side (404), which the checkout page handles; any other failure has
 * already been shown to the customer and left the cart as the server has it.
 */
export type TicketProtectionResult =
  | { ok: true }
  | { ok: false, cartMissing: boolean, error: unknown }

/**
 * Remembers the latest request so another part of the page can wait for it to
 * land — checkout must not read the payable total for Stripe Elements while a
 * Ticket Protection change is still on its way (MR !459 review).
 */
export const createPendingRequest = <T>() => {
  let current: Promise<T> | null = null

  return {
    run(request: () => Promise<T>): Promise<T> {
      const promise: Promise<T> = request().finally(() => {
        if (current === promise) current = null
      })
      current = promise
      return promise
    },
    /** Resolves once no request is in flight. Never rejects. */
    async settled(): Promise<void> {
      while (current) {
        await current.catch(() => undefined)
      }
    },
  }
}

/**
 * The checkout card's Ticket Protection switch and T&Cs modal (BOAT-1977).
 * The cart store owns the request and its pending state; this only turns the
 * customer's intent into a change and closes the modal once the change has
 * actually been made. A failed change keeps the modal open, showing the
 * cart's real state, so the customer can try again.
 */
export const useTicketProtectionSelection = (options: {
  selected: () => boolean
  /** Locked while paying, after an order exists, or while a change is saving. */
  blocked: () => boolean
  save: (selected: boolean) => Promise<TicketProtectionResult>
  onCartMissing: (error: unknown) => void
}) => {
  const modalOpen = ref(false)

  /** Resolves true once the cart has the requested selection. */
  const setSelected = async (value: boolean): Promise<boolean> => {
    if (value === options.selected()) return true
    if (options.blocked()) return false

    const result = await options.save(value)
    if (result.ok) return true

    if (result.cartMissing) {
      modalOpen.value = false
      options.onCartMissing(result.error)
    }
    return false
  }

  // Design: switching on adds TP straight away and shows the T&Cs, whose
  // buttons keep it or take it off again (the buttons wait while the add is
  // saving). Switching off just removes it.
  const toggle = () => {
    if (options.blocked()) return
    if (options.selected()) {
      setSelected(false)
      return
    }
    modalOpen.value = true
    setSelected(true)
  }
  const accept = async () => {
    if (await setSelected(true)) modalOpen.value = false
  }
  const decline = async () => {
    if (await setSelected(false)) modalOpen.value = false
  }

  return { modalOpen, toggle, accept, decline }
}
